import React, { useCallback, useEffect, useState } from 'react';
import {
    Alert, Box, Button, Chip, CircularProgress, FormControl, InputLabel, MenuItem, Paper, Select,
    Stack, Table, TableBody, TableCell, TableContainer, TableFooter, TableHead, TableRow, TextField,
    Typography,
} from '@mui/material';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import { MerchReportService } from '../../services/merchReport.service';
import { AuthService } from '../../services/auth.service';
import { useToast } from '../../context/ToastContext';
import { IBuyer } from '../../interfaces/garments/buyer.interface';
import { IOrderBook, IOrderBookFilters } from '../../interfaces/garments/merchReport.interface';
import { BuyerOrderStatus } from '../../interfaces/garments/buyerOrder.interface';
import { BuyerAutocomplete } from './BuyerAutocomplete';
import { triggerDownload } from '../statutoryRegisters/shared';
import { enumLabel } from '../../utils/garmentsLabels';
import { toLocalIsoDate, formatDate } from '../../utils/dateUtils';

const ORDER_STATUSES: BuyerOrderStatus[] = ['DRAFT', 'CONFIRMED', 'IN_PRODUCTION', 'SHIPPED', 'CLOSED', 'CANCELLED'];

const money = (value: number | null | undefined): string =>
    value === null || value === undefined
        ? '—'
        : value.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

/** Totals per currency (never adds EUR to USD). One currency renders like a row value. */
const currencyTotals = (
    rows: { totalValue?: number | null; currencyCode?: string | null }[],
    fallbackTotal: number,
): React.ReactNode => {
    const byCurrency = new Map<string, number>();
    rows.forEach(r => byCurrency.set(r.currencyCode ?? '', (byCurrency.get(r.currencyCode ?? '') ?? 0) + (r.totalValue ?? 0)));
    if (byCurrency.size <= 1) {
        const code = byCurrency.size === 1 ? Array.from(byCurrency.keys())[0] : '';
        return `${money(fallbackTotal)}${code ? ` ${code}` : ''}`;
    }
    return Array.from(byCurrency.entries()).map(([code, total]) => (
        <div key={code}>{money(total)}{code ? ` ${code}` : ''}</div>
    ));
};

/**
 * Order book report (garments-merchandising T11, spec
 * `.superpowers/sdd/2026-09-09-garments-merchandising/t11-brief.md`). Filter bar over
 * `MerchandisingReportController#getOrderBook`; renders one row per open buyer order plus a
 * per-buyer subtotal section and a grand-total footer row. Export XLSX streams the same filters
 * through `downloadOrderBookXlsx` and hands the blob to `triggerDownload` (G21).
 */
export function OrderBookReport() {
    const toast = useToast();

    const [report, setReport] = useState<IOrderBook | null>(null);
    const [loading, setLoading] = useState(true);
    const [downloading, setDownloading] = useState(false);

    const [buyerFilter, setBuyerFilter] = useState<IBuyer | null>(null);
    const [shipFrom, setShipFrom] = useState('');
    const [shipTo, setShipTo] = useState('');
    const [status, setStatus] = useState<BuyerOrderStatus | ''>('');
    const [includeAll, setIncludeAll] = useState(false);

    const buildFilters = useCallback((): IOrderBookFilters => ({
        buyerId: buyerFilter?.id || undefined,
        shipFrom: shipFrom || undefined,
        shipTo: shipTo || undefined,
        status: status || undefined,
        includeAll,
    }), [buyerFilter, shipFrom, shipTo, status, includeAll]);

    const load = useCallback(async () => {
        const institutionId = AuthService.getSelectedInstitution();
        if (!institutionId) return;
        setLoading(true);
        try {
            const data = await MerchReportService.getOrderBook(institutionId, buildFilters());
            setReport(data);
        } catch (e) {
            toast.showApiError(e, 'Failed to load order book');
        } finally {
            setLoading(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [buildFilters]);

    useEffect(() => {
        load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const clearFilters = () => {
        setBuyerFilter(null);
        setShipFrom('');
        setShipTo('');
        setStatus('');
        setIncludeAll(false);
    };

    const handleExport = async () => {
        const institutionId = AuthService.getSelectedInstitution();
        if (!institutionId) return;
        setDownloading(true);
        try {
            const blob = await MerchReportService.downloadOrderBookXlsx(institutionId, buildFilters());
            triggerDownload(blob, `order-book-${toLocalIsoDate()}.xlsx`);
        } catch (e) {
            toast.showApiError(e, 'Failed to export order book');
        } finally {
            setDownloading(false);
        }
    };

    return (
        <Box sx={{ p: 3 }} data-help-page="merchandising.orderBook.page">
            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
                <Typography variant="h5">Order Book</Typography>
                <Button
                    data-help="merchandising.orderBook.export"
                    variant="contained" size="small" startIcon={<FileDownloadIcon />}
                    onClick={handleExport} disabled={!report || downloading}
                >
                    Export XLSX
                </Button>
            </Stack>

            <Paper variant="outlined" sx={{ p: 2, mb: 2 }} data-help-section="merchandising.orderBook.filter">
                <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap alignItems="center">
                    <Box sx={{ flex: '1 1 220px', maxWidth: 340 }}>
                        <BuyerAutocomplete value={buyerFilter} onChange={setBuyerFilter} label="Buyer" size="small" />
                    </Box>
                    <TextField
                        data-help="merchandising.orderBook.shipFrom" size="small" label="Ship From" type="date" InputLabelProps={{ shrink: true }} sx={{ flex: '1 1 150px', maxWidth: 200 }}
                        value={shipFrom} onChange={e => setShipFrom(e.target.value)}
                    />
                    <TextField
                        data-help="merchandising.orderBook.shipTo" size="small" label="Ship To" type="date" InputLabelProps={{ shrink: true }} sx={{ flex: '1 1 150px', maxWidth: 200 }}
                        value={shipTo} onChange={e => setShipTo(e.target.value)}
                    />
                    <FormControl data-help="merchandising.orderBook.status" size="small" sx={{ flex: '1 1 160px', maxWidth: 280 }}>
                        <InputLabel>Status</InputLabel>
                        <Select
                            label="Status" value={status}
                            onChange={e => setStatus(e.target.value as BuyerOrderStatus | '')}
                        >
                            <MenuItem value="">All open</MenuItem>
                            {ORDER_STATUSES.map(s => <MenuItem key={s} value={s}>{enumLabel(s)}</MenuItem>)}
                        </Select>
                    </FormControl>
                    <FormControl data-help="merchandising.orderBook.scope" size="small" sx={{ flex: '1 1 150px', maxWidth: 270 }}>
                        <InputLabel>Scope</InputLabel>
                        <Select
                            label="Scope" value={includeAll ? 'all' : 'open'}
                            onChange={e => setIncludeAll(e.target.value === 'all')}
                        >
                            <MenuItem value="open">Open orders only</MenuItem>
                            <MenuItem value="all">Include closed/cancelled</MenuItem>
                        </Select>
                    </FormControl>
                    <Button data-help="merchandising.orderBook.apply" variant="outlined" size="small" onClick={load} disabled={loading}>Apply</Button>
                    <Button data-help="merchandising.orderBook.clear" size="small" onClick={clearFilters}>Clear</Button>
                </Stack>
            </Paper>

            {loading && (
                <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}><CircularProgress /></Box>
            )}

            {!loading && report && (
                <>
                    {new Set(report.rows.map(r => r.currencyCode ?? '')).size > 1 && (
                        <Alert severity="info" sx={{ mb: 1 }}>
                            Orders are in more than one currency; totals are shown per currency and are not converted.
                        </Alert>
                    )}
                    {report.caveats.map((c, i) => (
                        <Alert key={i} severity="info" sx={{ mb: 1 }}>{c}</Alert>
                    ))}

                    <TableContainer component={Paper} data-testid="order-book-table" data-help-section="merchandising.orderBook.table">
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell data-help="merchandising.orderBook.col.order">Order #</TableCell>
                                    <TableCell data-help="merchandising.orderBook.col.po">Buyer PO</TableCell>
                                    <TableCell data-help="merchandising.orderBook.col.buyer">Buyer</TableCell>
                                    <TableCell data-help="merchandising.orderBook.col.styles">Styles</TableCell>
                                    <TableCell data-help="merchandising.orderBook.col.shipDate">Ship Date</TableCell>
                                    <TableCell align="right" data-help="merchandising.orderBook.col.qty">Qty</TableCell>
                                    <TableCell align="right" data-help="merchandising.orderBook.col.value">Value</TableCell>
                                    <TableCell data-help="merchandising.orderBook.col.status">Status</TableCell>
                                    <TableCell align="right" data-help="merchandising.orderBook.col.daysToShip">Days To Ship</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {report.rows.length === 0 && (
                                    <TableRow>
                                        <TableCell colSpan={9} align="center">
                                            <Typography color="text.secondary" sx={{ py: 4 }}>
                                                No orders match this filter.
                                            </Typography>
                                        </TableCell>
                                    </TableRow>
                                )}
                                {report.rows.map(row => (
                                    <TableRow key={row.orderId} hover>
                                        <TableCell>{row.orderNumber}</TableCell>
                                        <TableCell>{row.buyerPoNumber ?? '—'}</TableCell>
                                        <TableCell>{row.buyerName}</TableCell>
                                        <TableCell>{row.styles}</TableCell>
                                        <TableCell sx={{ whiteSpace: 'nowrap' }}>{formatDate(row.shipDate)}</TableCell>
                                        <TableCell align="right">{row.totalQuantity ?? '—'}</TableCell>
                                        <TableCell align="right">
                                            {money(row.totalValue)}{row.currencyCode ? ` ${row.currencyCode}` : ''}
                                        </TableCell>
                                        <TableCell><Chip size="small" label={enumLabel(row.status)} /></TableCell>
                                        <TableCell align="right">{row.daysToShip ?? '—'}</TableCell>
                                    </TableRow>
                                ))}

                                {report.buyerTotals.map(bt => (
                                    <TableRow
                                        key={`subtotal-${bt.buyerId}`}
                                        data-testid={`order-book-subtotal-${bt.buyerId}`}
                                        sx={{ '& td': { fontWeight: 'bold', bgcolor: 'action.hover' } }}
                                    >
                                        <TableCell colSpan={2}>Subtotal — {bt.buyerName}</TableCell>
                                        <TableCell>{bt.orderCount} order(s)</TableCell>
                                        <TableCell />
                                        <TableCell />
                                        <TableCell align="right">{bt.totalQuantity}</TableCell>
                                        <TableCell align="right">
                                            {currencyTotals(report.rows.filter(r => r.buyerId === bt.buyerId), bt.totalValue)}
                                        </TableCell>
                                        <TableCell colSpan={2} />
                                    </TableRow>
                                ))}
                            </TableBody>
                            <TableFooter>
                                <TableRow
                                    data-testid="order-book-grand-total"
                                    sx={{ '& td': { fontWeight: 'bold', borderTop: '2px solid', borderColor: 'divider' } }}
                                >
                                    <TableCell colSpan={2}>Grand Total</TableCell>
                                    <TableCell>{report.grandTotal.orderCount} order(s)</TableCell>
                                    <TableCell />
                                    <TableCell />
                                    <TableCell align="right">{report.grandTotal.totalQuantity}</TableCell>
                                    <TableCell align="right">
                                        {currencyTotals(report.rows, report.grandTotal.totalValue)}
                                    </TableCell>
                                    <TableCell colSpan={2} />
                                </TableRow>
                            </TableFooter>
                        </Table>
                    </TableContainer>
                </>
            )}
        </Box>
    );
}

export default OrderBookReport;
